import axios from 'axios';

import { getDateAsUtcNoonInISO } from '../../../../services/datetime-service';

const STOCK_AUDITS_BASE_PATH = '/stock-audits';

export const StockAuditStatus = {
  assigned: 'Assigned',
  inProgress: 'InProgress',
} as const;

export type StockAuditStatus = (typeof StockAuditStatus)[keyof typeof StockAuditStatus];

type StockAuditProduct = {
  productId: string | number;
  inventoryLocationId: string | number;
};

type StockAuditDetailsProduct = {
  productId: string;
  inventoryLocationId: string;
  sku: string | null;
  productName: string | null;
};

export type StockAuditDetailsResponse = {
  auditId: string;
  auditName: string;
  dueDateUtc: string;
  inventoryWarehouseId: string;
  assigneeUserId: string | null;
  notes: string | null;
  products: StockAuditDetailsProduct[];
};

export const getStockAudit = async (auditId: string): Promise<StockAuditDetailsResponse> => {
  const response = await axios.get<StockAuditDetailsResponse>(`${STOCK_AUDITS_BASE_PATH}/${auditId}`);
  return response.data;
};

type CreateStockAuditRequest = {
  auditName: string;
  dueDate: string;
  assigneeUserId: string;
  notes: string;
  inventoryWarehouseId: string;
  products: StockAuditProduct[];
};

type CreateStockAuditPayload = {
  auditName: string;
  inventoryWarehouseId: string;
  dueDateUtc: string;
  assigneeUserId: string | null;
  notes: string | null;
  products: StockAuditProduct[];
};

export const createStockAudit = async (
  { auditName, dueDate, assigneeUserId, notes, inventoryWarehouseId, products }: CreateStockAuditRequest,
  dateFormat: string,
): Promise<void> => {
  const payload: CreateStockAuditPayload = {
    auditName,
    inventoryWarehouseId,
    dueDateUtc: getDateAsUtcNoonInISO(dueDate, dateFormat),
    assigneeUserId: assigneeUserId || null,
    notes: notes || null,
    products,
  };

  await axios.post(STOCK_AUDITS_BASE_PATH, payload);
};

type UpdateStockAuditRequest = {
  auditId: string;
  auditName: string;
  dueDate: string;
  assigneeUserId: string;
  notes: string;
};

type UpdateStockAuditPayload = {
  auditName: string;
  dueDateUtc: string;
  assigneeUserId: string | null;
  notes: string | null;
};

export const updateStockAudit = async (
  { auditId, auditName, dueDate, assigneeUserId, notes }: UpdateStockAuditRequest,
  dateFormat: string,
): Promise<void> => {
  const payload: UpdateStockAuditPayload = {
    auditName,
    dueDateUtc: getDateAsUtcNoonInISO(dueDate, dateFormat),
    assigneeUserId: assigneeUserId || null,
    notes: notes || null,
  };

  await axios.put(`${STOCK_AUDITS_BASE_PATH}/${auditId}`, payload);
};

export const cancelStockAudit = async (auditId: string): Promise<void> => {
  await axios.post(`${STOCK_AUDITS_BASE_PATH}/${auditId}/cancel`);
};
